import { Observable, combineLatest, map, switchMap } from 'rxjs';
import { LiquidityAmount } from '@akropolis-web/primitives';

import { protocolsApi, ratingsApi } from 'api/modules';
import {
  MarketRating,
  NetworkSlug,
  ProductSlug,
  ProtocolAvgRatingInfo,
  Rating,
  VersionSlug,
  isProtocolWeightedAvgRating,
} from 'domain/types';
import { Either, left, right } from 'utils/either';
import { ERROR_MESSAGES, getAmount } from 'domain/utils';
import { compareAmounts } from 'utils/amount';

export function getProtocolAvgRatingInfo({
  productSlug,
  versionSlug,
  network,
}: {
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
  network: NetworkSlug;
}): Observable<Either<ProtocolAvgRatingInfo>> {
  return combineLatest([
    protocolsApi
      .getLendingInfo$(true, { productSlug, versionSlug, network })
      .pipe(switchMap(({ id }) => ratingsApi.getProtocolRatings$(id))),
    protocolsApi.getLendingInfo$(true, { productSlug, versionSlug, network }),
    protocolsApi.getMarketsMetrics$('latest', { productSlug, versionSlug, network }),
    ratingsApi.getMarketsRatings$({ productSlug, versionSlug, network }),
  ]).pipe(
    map(([protocolRatings, protocolInfo, allMarketsMetrics, marketsRatings]) => {
      const protocolRating = protocolRatings.find(isProtocolWeightedAvgRating);

      if (!protocolRating) {
        return left(ERROR_MESSAGES.UNKNOWN_RATING);
      }

      const marketTvls = new Map(
        allMarketsMetrics.map(({ marketId, partialBalanceUSD }) => [
          marketId,
          partialBalanceUSD.totalDeposit,
        ]),
      );
      const marketNames = new Map(
        protocolInfo.markets.map(({ vid: id, marketName }) => [id, marketName]),
      );
      const allMarketsRatings = Array.from(marketsRatings.values()).flat();

      return right({
        rating: protocolRating,
        transcription: {
          productName: protocolInfo.fullName,
          hasBadDebtRating: allMarketsRatings.some(({ ratingType }) => ratingType === 'BAD_DEBT'),
          hasManipulationComplexityRating: allMarketsRatings.some(
            ({ ratingType }) => ratingType === 'MANIPULATION_COMPLEXITY',
          ),
          worstBadDebtMarkets: getWorstMarketRatingsByType(
            allMarketsRatings,
            marketTvls,
            marketNames,
            'BAD_DEBT',
          ),
          worstManipulationComplexityMarkets: getWorstMarketRatingsByType(
            allMarketsRatings,
            marketTvls,
            marketNames,
            'MANIPULATION_COMPLEXITY',
          ),
        },
      });
    }),
  );
}

function getWorstMarketRatingsByType(
  allMarketRatings: MarketRating[],
  marketTvls: Map<string, LiquidityAmount>,
  marketNames: Map<string, string>,
  type: Exclude<MarketRating['ratingType'], 'UNKNOWN'>,
): ProtocolAvgRatingInfo['transcription']['worstBadDebtMarkets'] {
  const groupedRatings = allMarketRatings.reduce<
    Record<number, { rating: Rating; weight: LiquidityAmount; ratings: MarketRating[] }>
  >((acc, curRating) => {
    if (curRating.ratingType !== type && curRating.rating > 1) {
      return acc;
    }

    const currentGroup = acc[curRating.rating] || {
      rating: curRating.rating,
      weight: getAmount(0, '$'),
      ratings: [],
    };
    const marketTvl = marketTvls.get(curRating.marketVid) || getAmount(0, '$');

    return {
      ...acc,
      [curRating.rating]: {
        ...currentGroup,
        weight: currentGroup.weight.add(marketTvl.mul(curRating.rating)),
        ratings: [...currentGroup.ratings, curRating],
      },
    };
  }, {});

  const worstGroup = Object.values(groupedRatings).sort((a, b) =>
    compareAmounts(a.weight, b.weight),
  )[0];

  return worstGroup
    ? {
        rating: worstGroup.rating,
        marketNames: worstGroup.ratings
          .map(({ marketVid }) => marketNames.get(marketVid) || '')
          .filter(Boolean),
      }
    : null;
}
